// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';

import ProductCardComponent from 'Components/Templates/ProductCardComponent';
import { API_PRODUCTS } from 'Constants';
import { ToastNotificationsContext } from 'Contexts/ToastNotificationsContext';
import useCart from 'Hooks/useCart';
import useCategories from 'Hooks/useCategories';
import useFetch from 'Hooks/useFetch';
import useModals from 'Hooks/useModals';
import useUser from 'Hooks/useUser';

// Module-level cache + in-flight dedupe so a given product URL is only ever
// fetched once. The data survives card re-renders and remounts (the Puck
// template subtree can re-render when the cart changes), so the card never
// blanks out or refetches once it has loaded a given URL.
const productCache = new Map();
const inFlightRequests = new Map();

const fetchProductData = (fetcher, url) => {
  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url);
  }

  const request = (async () => {
    const data = await fetcher(url);
    let pictureUrl = data?.pictureUrl;
    if (
      data?.pictures?.length
      && (
        !pictureUrl
        || pictureUrl.includes('no-image-available')
      )
    ) {
      pictureUrl = data.pictures.at(0)?.urlOverlayPicture;
    }
    const normalized = {
      ...data,
      pictureUrl,
      hasPrice: data?.hasPrice ?? data?.price !== null,
      quantity: data?.quantity || data?.minQuantity || 1,
    };
    productCache.set(url, normalized);
    return normalized;
  })();

  inFlightRequests.set(url, request);
  request.finally(() => inFlightRequests.delete(url));
  return request;
};

const ProductCard = ({ productId, title, specs }) => {
  const fetcher = useFetch();
  const cart = useCart();
  const { data: categories, findActiveCategoryParts } = useCategories();
  const modalsContext = useModals();
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { email, defaultCurrency: currency, withVat: vat } = user || {};
  const addNotification = useContextSelector(
    ToastNotificationsContext,
    context => context.addNotification
  );
  // Build a stable request URL. Depending on this string (compared by value)
  // means unrelated re-renders (cart/user/toast/modal updates) never re-trigger
  // the fetch, so the card is only ever fetched when productId or pricing changes.
  const productUrl = (productId && email)
    ? `${ API_PRODUCTS }/${ encodeURIComponent(productId) }?${ new URLSearchParams({
      uid: email,
      currency: currency || 'RON',
      vat: String(vat ?? false),
    }) }`
    : null;

  // Initialise straight from the module cache so a remount of the card (which the
  // Puck template can trigger on cart changes) renders the product immediately,
  // with no blank/loading flash.
  const [product, setProduct] = useState(() => (productUrl ? productCache.get(productUrl) || null : null));
  const [loading, setLoading] = useState(() => Boolean(productUrl) && !productCache.has(productUrl));
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    let active = true;

    if (!productUrl) {
      setProduct(null);
      setError(null);
      setLoading(false);
      return () => { active = false; };
    }

    // Serve from the module cache first: this survives re-renders AND remounts
    // of the card, so once a URL has loaded the card never blanks or refetches.
    const cached = productCache.get(productUrl);
    if (cached) {
      setProduct(cached);
      setError(null);
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    setError(null);
    fetchProductData(fetcher, productUrl)
      .then(data => {
        if (active) setProduct(data);
      })
      .catch(requestError => {
        if (active) {
          setProduct(null);
          setError(requestError);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [fetcher, productUrl]);

  const executeAddToCart = async () => {
    if (!product) return null;

    const cartProduct = (cart?.data?.items || [])
      .find(item => item.productId === product.id && !item.promotion?.promotionId);
    const quantity = Math.max(product.minQuantity, product.quantity);

    if (((cartProduct?.quantity || 0) + quantity) > product.stockValue) {
      addNotification({
        type: 'warning',
        title: t('products.insufficientStock'),
      });
      return null;
    }

    setAddingToCart(true);
    try {
      const [updated, crossProducts] = await Promise.allSettled([
        cart.updateProduct({
          ...product,
          id: product.id,
          quantity: Math.min(
            product.stockValue,
            (cartProduct?.quantity || 0) + quantity
          ),
        }),
        fetcher(`${ API_PRODUCTS }/${ product.id }/cross-sells?currency=${ currency }&vat=${ vat }`),
      ]);

      if (updated.status === 'fulfilled') {
        if (updated.value === null) {
          addNotification({
            type: 'warning',
            title: t('products.dropShipmentWarning'),
          });
        } else {
          addNotification({
            title: product.promotion
              ? t('cart.successfullyAddedPromotionToCart')
              : t('cart.successfullyAddedToCart'),
          });
        }
      } else {
        addNotification({
          type: 'error',
          title: t('cart.failedAddToCart'),
          message: updated.reason?.info?.errors?.at(0)?.message,
        });
        return null;
      }

      if (Array.isArray(crossProducts.value) && crossProducts.value.length) {
        modalsContext.setState(modalsContext.modalState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product,
        });
      }
      return updated.value;
    } catch (cartError) {
      addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: cartError.info?.errors?.at(0)?.message,
      });
    } finally {
      setAddingToCart(false);
    }
    return null;
  };

  const handleAddToCart = () => {
    if (!product) return null;

    if (!product.hasPrice) {
      modalsContext.setState(
        modalsContext.modalState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key,
        {
          open: true,
          product,
          onConfirm: executeAddToCart,
        }
      );
      return null;
    }

    return executeAddToCart();
  };

  const categoryId = Number(product?.categoryId);
  const category = Number.isFinite(categoryId)
    ? findActiveCategoryParts(categories, categoryId)?.at(-1)
    : null;
  const categoryPath = category?.slug && category?.id
    ? `/products/${ category.slug }-c${ category.id }/c`
    : null;

  return (
    <ProductCardComponent
      product={ product }
      loading={ loading }
      error={ error }
      addingToCart={ addingToCart }
      onAddToCart={ handleAddToCart }
      categoryPath={ categoryPath }
      title={ title }
      specs={ specs }
    />
  );
};

ProductCard.propTypes = {
  productId: string,
  title: string,
  specs: arrayOf(shape({
    label: string,
    value: string,
  })),
};

export default ProductCard;
